"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "../lib/utils"
import { overlayBackdrop } from "../styles/recipes.js"
import {
  OverlayFocusContext,
  restoreOverlayFocus,
  useOverlayFocus,
} from "../lib/controlled-overlay-focus.js"
import { PortalContainerContext } from "./portal-container.jsx"

function Sheet({
  children,
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
  ...props
}) {
  const { focusContext, handleOpenChange } = useOverlayFocus(open, defaultOpen, onOpenChange, modal)
  return (
    <OverlayFocusContext.Provider value={focusContext}>
      <SheetPrimitive.Root
        data-slot="sheet"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        modal={modal}
        {...props}>
        {children}
      </SheetPrimitive.Root>
    </OverlayFocusContext.Provider>
  )
}

const SheetTrigger = React.forwardRef(({ onClick, ...props }, ref) => {
  const focusContext = React.useContext(OverlayFocusContext)
  return (
    <SheetPrimitive.Trigger
      ref={ref}
      data-slot="sheet-trigger"
      {...props}
      onClick={(event) => {
        focusContext?.captureInvoker(event.currentTarget)
        onClick?.(event)
      }} />
  )
});
SheetTrigger.displayName = SheetPrimitive.Trigger.displayName;

const SheetClose = React.forwardRef(({ ...props }, ref) => (
  <SheetPrimitive.Close ref={ref} data-slot="sheet-close" {...props} />
));
SheetClose.displayName = SheetPrimitive.Close.displayName;

function SheetPortal({
  container,
  ...props
}) {
  const portalContainer = React.useContext(PortalContainerContext)
  return <SheetPrimitive.Portal container={container === undefined ? portalContainer : container} data-slot="sheet-portal" {...props} />;
}

const SheetOverlay = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    data-slot="sheet-overlay"
    className={cn(
      `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-[var(--ds-layer-sheet-backdrop)] ${overlayBackdrop}`,
      className
    )}
    {...props} />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const SheetContent = React.forwardRef(({
  className,
  children,
  side = "right",
  container,
  onCloseAutoFocus,
  ...props
}, ref) => {
  const { t } = useTranslation("common")
  const focusContext = React.useContext(OverlayFocusContext)
  const handleCloseAutoFocus = (event) => {
    onCloseAutoFocus?.(event)
    restoreOverlayFocus(event, focusContext)
  }
  return (
    <SheetPortal container={container}>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "bg-card text-card-foreground data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-[var(--ds-layer-sheet)] flex flex-col gap-[var(--ds-space-4)] border-border shadow-[var(--shadow-command)] transition ease-in-out data-[state=closed]:duration-[var(--ds-motion-duration-standard)] data-[state=open]:duration-[var(--ds-motion-duration-slow)]",
          side === "right" &&
          "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-[var(--ds-overlay-sheet-width)] border-l sm:max-w-[var(--ds-overlay-sheet-max-width)]",
          side === "left" &&
          "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-[var(--ds-overlay-sheet-width)] border-r sm:max-w-[var(--ds-overlay-sheet-max-width)]",
          side === "top" &&
          "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
          side === "bottom" &&
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
          className
        )}
        onCloseAutoFocus={handleCloseAutoFocus}
        {...props}>
        {children}
        <SheetPrimitive.Close
          data-slot="sheet-close"
          aria-label={t("actions.close", { defaultValue: "Close" })}
          className="ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent absolute top-[var(--ds-space-3)] right-[var(--ds-space-3)] rounded-[var(--ds-radius-control)] p-[var(--ds-space-2)] opacity-70 transition-opacity hover:bg-accent hover:opacity-100 focus-visible:ring-[var(--ds-focus-ring-width)] focus-visible:ring-offset-[var(--ds-focus-ring-offset)] focus-visible:outline-hidden disabled:pointer-events-none">
          <XIcon className="size-[var(--ds-icon-size-sm)]" />
          <span className="sr-only">{t("actions.close", { defaultValue: "Close" })}</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <div
    ref={ref}
    data-slot="sheet-header"
    className={cn("flex flex-col gap-[var(--ds-space-1-5)] border-b border-border-subtle p-[var(--ds-space-panel-compact)]", className)}
    {...props} />
));
SheetHeader.displayName = "SheetHeader";

const SheetFooter = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <div
    ref={ref}
    data-slot="sheet-footer"
    className={cn("mt-auto flex flex-col gap-[var(--ds-space-2)] border-t border-border-subtle p-[var(--ds-space-panel-compact)]", className)}
    {...props} />
));
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    data-slot="sheet-title"
    className={cn("text-foreground font-semibold tracking-[-0.02em]", className)}
    {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef(({
  className,
  ...props
}, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    data-slot="sheet-description"
    className={cn("text-muted-foreground text-sm leading-5", className)}
    {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
